/*
 * Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
 * Distributed under the TruVideo proprietary license.
 */

/**
 * multipartUpload.js
 *
 * Browser-direct multipart upload to S3 via TruVideo presigned URLs.
 * All AWS/TruVideo credentials stay on the server — the browser only
 * receives short-lived presigned PUT URLs.
 *
 * Flow:
 *   1. POST /api/capture/:token/upload/initiate  → receive presigned part URLs
 *   2. For each part: PUT presignedUrl (native fetch — no axios, no credentials)
 *   3. POST /api/capture/:token/upload/:uploadId/complete  → receive final fileUrl
 */

import { getSessionToken } from './cookieService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/** 25 MB chunk size — must match server constant */
export const CHUNK_SIZE_BYTES = 25 * 1024 * 1024;

/** Maximum file size supported by multipart upload: 1 GB */
export const MAX_FILE_SIZE_BYTES = 550 * 1024 * 1024;

/** Image extensions that must NOT use multipart upload */
const IMAGE_EXTENSIONS = new Set([
  'jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'heic', 'avif',
]);

/**
 * Returns true when the given file should use the multipart flow.
 * Images always stay on the legacy path (photo/identifier/image file-step).
 *
 * @param {File} file
 * @returns {boolean}
 */
export function shouldUseMultipartUpload(file) {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (IMAGE_EXTENSIONS.has(ext)) return false;
  if (file.type.startsWith('image/')) return false;
  return true;
}

/**
 * Compute part breakdown for a given file size.
 *
 * @param {number} fileSize
 * @returns {{ partCount: number, chunkSize: number }}
 */
export function calculateParts(fileSize) {
  const partCount = Math.ceil(fileSize / CHUNK_SIZE_BYTES);
  return { partCount, chunkSize: CHUNK_SIZE_BYTES };
}

/**
 * Upload a file using multipart presigned URLs.
 *
 * @param {Object}   opts
 * @param {string}   opts.token             - Capture assignment token
 * @param {File}     opts.file              - The File object selected/recorded by the user
 * @param {string}   opts.assignmentItemId  - Assignment item UUID
 * @param {Object}   [opts.assignmentItem]  - Full assignment item metadata (serialised to JSON)
 * @param {number}   [opts.duration]        - Video duration in ms
 * @param {Object}   [opts.provenanceData]  - Provenance payload
 * @param {string}   [opts.identifierText]  - OCR identifier text (identifier step)
 * @param {string}   [opts.customerIdentifierText] - Customer-editable identifier
 * @param {string}   [opts.capturedByName]  - Name of the person submitting this capture
 * @param {string}   [opts.capturedByEmail] - Email of the person submitting this capture
 * @param {Function} [opts.onProgress]      - (percent: number) => void  (0–100)
 * @param {AbortSignal} [opts.signal]       - AbortSignal to cancel in-flight uploads
 * @param {boolean}  [opts.isOfflineReplay] - True only when this is a replay of a
 *   previously-queued offline mutation (never a live upload) — lets the server bypass
 *   its link-expired block for this request. See CaptureService.validateToken.
 * @param {string}   [opts.sessionTokenOverride] - Fallback session token to use when
 *   the cookie is empty. Only ever needed on a replay: the cookie is deliberately
 *   capped to expire at the assignment's absolute deadline (see cookieService), so a
 *   replay firing after that deadline finds no cookie at all. captureSyncQueue passes
 *   the value persisted in offlineDb (see offlineDb.setSessionCredential) for this.
 *
 * @returns {Promise<{ success, fileUrl, mediaId, identifierText, customerIdentifierText }>}
 */
export async function uploadFile({
  token,
  file,
  assignmentItemId,
  assignmentItem,
  duration,
  provenanceData,
  identifierText,
  customerIdentifierText,
  capturedByName,
  capturedByEmail,
  localId,
  onProgress,
  signal,
  isOfflineReplay = false,
  sessionTokenOverride = null,
}) {
  if (!file) throw new Error('No file provided');
  if (!token) throw new Error('token is required');
  if (!assignmentItemId) throw new Error('assignmentItemId is required');

  if (file.size === 0) throw new Error('File is empty');

  if (file.size > MAX_FILE_SIZE_BYTES) {
    throw new Error(
      `File size (${formatBytes(file.size)}) exceeds the maximum of ${formatBytes(MAX_FILE_SIZE_BYTES)}`,
    );
  }

  const sessionToken = getSessionToken(token) || sessionTokenOverride;
  const baseUrl = `${API_URL}/api/capture/${token}`;

  // ── Step 1: Initiate ────────────────────────────────────────────────────────
  const initiateBody = {
    assignmentItemId,
    fileName: file.name,
    fileSize: file.size,
    mimeType: file.type || 'application/octet-stream',
  };
  if (assignmentItem) initiateBody.assignmentItem = JSON.stringify(assignmentItem);
  if (duration != null) initiateBody.duration = duration;
  if (provenanceData) initiateBody.provenanceData = JSON.stringify(provenanceData);
  if (identifierText) initiateBody.identifierText = identifierText;
  if (customerIdentifierText) initiateBody.customerIdentifierText = customerIdentifierText;
  if (isOfflineReplay) initiateBody.isOfflineReplay = true;

  const initiateHeaders = { 'Content-Type': 'application/json' };
  if (sessionToken) initiateHeaders['x-session-token'] = sessionToken;

  const initiateResp = await fetch(`${baseUrl}/upload/initiate`, {
    method: 'POST',
    headers: initiateHeaders,
    signal,
    body: JSON.stringify(initiateBody),
  });

  if (!initiateResp.ok) {
    const errBody = await initiateResp.json().catch(() => ({}));
    throw new Error(errBody?.message ?? `Initiate failed (${initiateResp.status})`);
  }

  const { uploadId, parts: presignedParts, partCount } = await initiateResp.json();

  // ── Step 2: Upload each part sequentially ───────────────────────────────────
  const etags = [];
  let uploadedBytes = 0;

  for (let i = 0; i < partCount; i++) {
    if (signal?.aborted) throw new DOMException('Upload cancelled', 'AbortError');

    const partNumber = i + 1;
    const start = i * CHUNK_SIZE_BYTES;
    const end = Math.min(start + CHUNK_SIZE_BYTES, file.size);
    const chunk = file.slice(start, end);

    const presignedUrl = presignedParts.find((p) => p.partNumber === partNumber)?.presignedUrl;

    if (!presignedUrl) {
      throw new Error(`Missing presigned URL for part ${partNumber}`);
    }

    const eTag = await uploadPartWithRetry({
      partNumber,
      chunk,
      presignedUrl,
      mimeType: file.type || 'application/octet-stream',
      signal,
      onRefreshUrl: async () => {
        const freshParts = await fetchFreshParts(baseUrl, uploadId, 1, sessionToken, isOfflineReplay);
        const fresh = freshParts.find((p) => p.partNumber === partNumber);
        if (!fresh) throw new Error(`Could not refresh URL for part ${partNumber}`);
        return fresh.presignedUrl;
      },
    });

    etags.push({ partNumber, eTag });
    uploadedBytes += end - start;

    if (onProgress) {
      onProgress(Math.round((uploadedBytes / file.size) * 100));
    }
  }

  // ── Step 3: Complete ────────────────────────────────────────────────────────
  const completeBody = {
    assignmentItemId,
    parts: etags,
  };
  if (duration != null) completeBody.duration = duration;
  if (provenanceData) completeBody.provenanceData = JSON.stringify(provenanceData);
  if (identifierText) completeBody.identifierText = identifierText;
  if (customerIdentifierText) completeBody.customerIdentifierText = customerIdentifierText;
  if (capturedByName) completeBody.capturedByName = capturedByName;
  if (capturedByEmail) completeBody.capturedByEmail = capturedByEmail;
  // Sent so /complete can recognize a replay of a lost-response completion as
  // the same logical upload instead of inserting a duplicate media file —
  // see CaptureService.completeUpload's dedupe-by-localId check.
  if (localId) completeBody.localId = localId;
  if (isOfflineReplay) completeBody.isOfflineReplay = true;

  const completeHeaders = { 'Content-Type': 'application/json' };
  if (sessionToken) completeHeaders['x-session-token'] = sessionToken;

  const completeResp = await fetch(`${baseUrl}/upload/${uploadId}/complete`, {
    method: 'POST',
    headers: completeHeaders,
    signal,
    body: JSON.stringify(completeBody),
  });

  if (!completeResp.ok) {
    const errBody = await completeResp.json().catch(() => ({}));
    throw new Error(errBody?.message ?? `Complete failed (${completeResp.status})`);
  }

  return await completeResp.json();
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Uploads a single chunk to S3 with up to 3 retries and exponential backoff.
 * On 403/400 (expired presigned URL) it refreshes the URL and retries immediately.
 */
async function uploadPartWithRetry({
  partNumber,
  chunk,
  presignedUrl,
  mimeType,
  signal,
  onRefreshUrl,
}) {
  const MAX_ATTEMPTS = 3;
  let url = presignedUrl;

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    if (signal?.aborted) throw new DOMException('Upload cancelled', 'AbortError');

    try {
      const resp = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': mimeType },
        body: chunk,
        signal,
      });

      if (resp.status === 403 || resp.status === 400) {
        if (attempt === MAX_ATTEMPTS) {
          throw new Error(
            `Part ${partNumber} failed after ${MAX_ATTEMPTS} attempts: HTTP ${resp.status}`,
          );
        }

        console.warn(`Part ${partNumber}: got ${resp.status}, refreshing presigned URL (attempt ${attempt})`);
        url = await onRefreshUrl();
        continue;
      }

      if (!resp.ok) {
        throw new Error(`S3 PUT failed for part ${partNumber}: HTTP ${resp.status}`);
      }

      // ETag comes back in the response header — strip surrounding quotes
      const rawETag = resp.headers.get('ETag') ?? resp.headers.get('etag') ?? '';
      const eTag = rawETag.replace(/"/g, '');

      if (!eTag) {
        throw new Error(`No ETag in S3 response for part ${partNumber}`);
      }

      return eTag;
    } catch (err) {
      if (err.name === 'AbortError') throw err;

      if (attempt === MAX_ATTEMPTS) {
        throw new Error(
          `Part ${partNumber} failed after ${MAX_ATTEMPTS} attempts: ${err.message}`,
        );
      }

      // Exponential backoff: 1s, 2s, 4s
      const delay = Math.pow(2, attempt - 1) * 1000;
      console.warn(`Part ${partNumber} attempt ${attempt} failed, retrying in ${delay}ms…`, err.message);
      await sleep(delay);
    }
  }
}

/**
 * Fetch fresh presigned URLs from the server for a given uploadId.
 */
async function fetchFreshParts(baseUrl, uploadId, count, sessionToken, isOfflineReplay = false) {
  const headers = {};
  if (sessionToken) headers['x-session-token'] = sessionToken;

  const query = `count=${count}${isOfflineReplay ? '&isOfflineReplay=true' : ''}`;
  const resp = await fetch(`${baseUrl}/upload/${uploadId}/parts?${query}`, { headers });
  if (!resp.ok) {
    const errBody = await resp.json().catch(() => ({}));
    throw new Error(errBody?.message ?? `Failed to refresh presigned URLs (${resp.status})`);
  }
  const { parts } = await resp.json();
  return parts;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Shared implementation behind uploadReferenceMediaFile and
 * uploadGlobalInstructionFile below — both hit the same authenticated
 * workflow-builder multipart initiate/parts/complete pattern (bearer token,
 * not a capture session token) and differ only in which endpoint they call.
 *
 * Flow:
 *   1. POST {baseUrl}/initiate
 *   2. For each part: PUT presignedUrl (native fetch)
 *   3. POST {baseUrl}/:uploadId/complete
 *
 * @param {string}   baseUrl
 * @param {Object}   opts
 * @param {File}     opts.file        - The file selected by the user
 * @param {Function} [opts.onProgress] - (percent: number) => void  (0–100)
 * @param {AbortSignal} [opts.signal]  - AbortSignal to cancel in-flight uploads
 *
 * @returns {Promise<{ url: string }>}
 */
async function uploadToWorkflowMultipartEndpoint(baseUrl, { file, onProgress, signal }) {
  if (!file) throw new Error('No file provided');
  if (file.size === 0) throw new Error('File is empty');

  const authToken = localStorage.getItem('auth_access_token');
  const authHeaders = authToken ? { Authorization: `Bearer ${authToken}` } : {};

  // ── Step 1: Initiate ────────────────────────────────────────────────────────
  const initiateResp = await fetch(`${baseUrl}/initiate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    signal,
    body: JSON.stringify({
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type || 'application/octet-stream',
    }),
  });

  if (!initiateResp.ok) {
    const errBody = await initiateResp.json().catch(() => ({}));
    throw new Error(errBody?.message ?? `Initiate failed (${initiateResp.status})`);
  }

  const { uploadId, parts: presignedParts, partCount } = await initiateResp.json();

  // ── Step 2: Upload each part sequentially ───────────────────────────────────
  const etags = [];
  let uploadedBytes = 0;

  for (let i = 0; i < partCount; i++) {
    if (signal?.aborted) throw new DOMException('Upload cancelled', 'AbortError');

    const partNumber = i + 1;
    const start = i * CHUNK_SIZE_BYTES;
    const end = Math.min(start + CHUNK_SIZE_BYTES, file.size);
    const chunk = file.slice(start, end);

    const presignedUrl = presignedParts.find((p) => p.partNumber === partNumber)?.presignedUrl;

    if (!presignedUrl) {
      throw new Error(`Missing presigned URL for part ${partNumber}`);
    }

    const eTag = await uploadPartWithRetry({
      partNumber,
      chunk,
      presignedUrl,
      mimeType: file.type || 'application/octet-stream',
      signal,
      onRefreshUrl: async () => {
        const resp = await fetch(`${baseUrl}/${uploadId}/parts?count=1`, { headers: authHeaders });
        if (!resp.ok) {
          const errBody = await resp.json().catch(() => ({}));
          throw new Error(errBody?.message ?? `Failed to refresh presigned URL (${resp.status})`);
        }
        const { parts: freshParts } = await resp.json();
        const fresh = freshParts.find((p) => p.partNumber === partNumber);
        if (!fresh) throw new Error(`Could not refresh URL for part ${partNumber}`);
        return fresh.presignedUrl;
      },
    });

    etags.push({ partNumber, eTag });
    uploadedBytes += end - start;

    if (onProgress) {
      onProgress(Math.round((uploadedBytes / file.size) * 100));
    }
  }

  // ── Step 3: Complete ────────────────────────────────────────────────────────
  const completeResp = await fetch(`${baseUrl}/${uploadId}/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    signal,
    body: JSON.stringify({ parts: etags }),
  });

  if (!completeResp.ok) {
    const errBody = await completeResp.json().catch(() => ({}));
    throw new Error(errBody?.message ?? `Complete failed (${completeResp.status})`);
  }

  return await completeResp.json();
}

/**
 * Upload a reference-media video via the authenticated workflow-builder
 * multipart endpoints. See uploadToWorkflowMultipartEndpoint for the flow.
 *
 * @param {Object}   opts
 * @param {File}     opts.file        - The video file selected by the user
 * @param {Function} [opts.onProgress] - (percent: number) => void  (0–100)
 * @param {AbortSignal} [opts.signal]  - AbortSignal to cancel in-flight uploads
 *
 * @returns {Promise<{ url: string }>}
 */
export async function uploadReferenceMediaFile({ file, onProgress, signal }) {
  return uploadToWorkflowMultipartEndpoint(`${API_URL}/api/workflows/upload-reference-media`, { file, onProgress, signal });
}

/**
 * Upload a workflow-wide global instruction file (video or PDF) via the
 * authenticated workflow-builder multipart endpoints — the separate
 * upload-global-instruction routes, so per-step reference media's mime
 * allow-list (image/video only) stays untouched. See
 * uploadToWorkflowMultipartEndpoint for the flow.
 *
 * @param {Object}   opts
 * @param {File}     opts.file        - The video or PDF file selected by the user
 * @param {Function} [opts.onProgress] - (percent: number) => void  (0–100)
 * @param {AbortSignal} [opts.signal]  - AbortSignal to cancel in-flight uploads
 *
 * @returns {Promise<{ url: string }>}
 */
export async function uploadGlobalInstructionFile({ file, onProgress, signal }) {
  return uploadToWorkflowMultipartEndpoint(`${API_URL}/api/workflows/upload-global-instruction`, { file, onProgress, signal });
}

export function formatBytes(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}
